import type { LocalDate } from './time';

/**
 * LOS TROFEOS — de metas y sueños cumplidos (spec 2026-10-30, bloque 2).
 *
 * Cuatro formas, una por trimestre, porque los trimestres son las temporadas de
 * Self. Una meta toma la forma del trimestre en que se cumple; un sueño, la que
 * elige el usuario. Los trofeos no se guardan aparte: salen de las metas y los
 * sueños cumplidos, así que no hay nada que pueda quedar desincronizado.
 */

export type FormaTrofeo = 'monolito' | 'gema' | 'constelacion' | 'medalla';

export const FORMAS: readonly {
  clave: FormaTrofeo;
  nombre: string;
  frase: string;
  detalle: string;
}[] = [
  {
    clave: 'monolito',
    nombre: 'Monolito',
    frase: 'Los cimientos',
    detalle: 'Lo que logras al empezar el año sostiene lo demás.',
  },
  {
    clave: 'gema',
    nombre: 'Gema tallada',
    frase: 'La presión que da forma',
    detalle: 'A mitad de año ya estás puliendo.',
  },
  {
    clave: 'constelacion',
    nombre: 'Constelación',
    frase: 'El camino',
    detalle: 'Mirando atrás, los pasos dibujan algo.',
  },
  {
    clave: 'medalla',
    nombre: 'Medalla acuñada',
    frase: 'El cierre',
    detalle: 'El año se acuña en metal.',
  },
];

export function esFormaTrofeo(valor: unknown): valor is FormaTrofeo {
  return FORMAS.some((f) => f.clave === valor);
}

/** La forma del trimestre de una fecha: ene–mar monolito … oct–dic medalla. */
export function formaDelTrimestre(fecha: LocalDate): FormaTrofeo {
  const mes = Number(fecha.slice(5, 7));
  const trimestre = Math.min(3, Math.max(0, Math.floor((mes - 1) / 3)));
  return FORMAS[trimestre]?.clave ?? 'monolito';
}

/** Cuánto está aclarado un trofeo, de 0 a 1. Sin pasos, nada. */
export function progresoDeTrofeo(hechos: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(1, Math.max(0, hechos / total));
}

export type Trofeo = {
  id: string;
  tipo: 'meta' | 'sueno';
  titulo: string;
  areaId: string;
  forma: FormaTrofeo;
  fecha: LocalDate;
  /** Lo que el usuario escribió que fue ese sueño. Solo en los sueños. */
  frase: string | null;
};

/** Las baldas de la vitrina: un año por balda, el más reciente arriba. */
export function trofeosPorAnio(trofeos: readonly Trofeo[]): { anio: string; trofeos: Trofeo[] }[] {
  const porAnio = new Map<string, Trofeo[]>();
  for (const t of trofeos) {
    const anio = t.fecha.slice(0, 4);
    porAnio.set(anio, [...(porAnio.get(anio) ?? []), t]);
  }
  return [...porAnio.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([anio, lista]) => ({
      anio,
      trofeos: lista.sort((a, b) => a.fecha.localeCompare(b.fecha)),
    }));
}
